import { useMemo } from 'react';
import { Button, Form, Modal, Result } from 'antd';

import { BundleWithChildren } from '@app/domain-bundle-interfaces';
import { Auth, FeatureFlags } from '@app/web-components';

import { useCreateBundle, useFindSubscriptionAccess } from '../data';

import { BundleFormData, useBundleForm } from './use-bundle-form';

type CreateFormProps = {
  record?: BundleWithChildren;
};

export const CreateForm = ({ record }: CreateFormProps) => {
  const createBundle = useCreateBundle();
  const { isFeatureEnabled } = FeatureFlags.useFeatureFlags();
  const { user } = Auth.useUserAuth();

  const initialValues: BundleFormData = useMemo(
    () => ({
      ...record,
      name: record?.name ?? '',
      productFamily: record?.productFamily
        ? { value: record.productFamily.externalId, label: record.productFamily.name }
        : undefined,
      accesses: record?.accesses.map((x) => x.access) ?? [],
      owners:
        record?.owners.map((x) => ({
          value: x.owner.enterpriseId,
          label: x.owner.name,
        })) ?? (user ? [{ label: user.name, value: user.enterpriseId }] : []),
      accessDependencies:
        record?.accessDependencies?.map((d) => ({ child: d.child.access, parent: d.parent.access })) ?? [],
      isActive: true,
      justificationRequired: record?.justificationRequired ?? false,
      pullSubscriptionEnabled: record?.pullSubscriptionEnabled ?? false,
      planningStateValidationEnabled: record?.justificationRequired ?? true,
      pullSubscriptionRunning: false,
      anchors: record?.accesses.filter((x) => x.isEagerAnchor).map((x) => x.access) ?? [],
    }),
    [record, user],
  );

  const queryResult = useFindSubscriptionAccess(
    {
      where: { bundleAccess: { bundleId: record?.id }, subscribed: true },
    },
    isFeatureEnabled('copy_subscription_accesses') && !!record,
  );

  const {
    getCreateAnotherButtonProps,
    getFormProps,
    getManageDependenciesModalProps,
    getSubscriptionAccessesFormItemProps,
    getSuccessResultProps,
  } = useBundleForm(createBundle, initialValues);

  if (createBundle.isSuccess) {
    return (
      <Result
        {...getSuccessResultProps({ extra: <Button key="create-another" {...getCreateAnotherButtonProps()} /> })}
        subTitle={`Bundle name: ${createBundle.data.data.mutated?.name}`}
        title="Successfully created a bundle!"
      />
    );
  }

  return (
    <>
      <Modal {...getManageDependenciesModalProps()} />
      <Form
        {...getFormProps(
          record && isFeatureEnabled('copy_subscription_accesses')
            ? { children: <Form.Item {...getSubscriptionAccessesFormItemProps(queryResult)} /> }
            : undefined,
        )}
      />
    </>
  );
};
